import type { CompletionStatus } from '~/helpers/learner/session-view'
import { useTranslation } from 'react-i18next'

export type StatusMeta = {
  /** Classe de fond du badge, en jetons de statut — jamais la couleur de marque. */
  badgeVariant: 'successSubtle' | 'warningSubtle' | 'secondary'
  badgeLabel: string
}

/**
 * Libellé et variante de badge par état. Le bouton d'une étape ne se déduit
 * pas du statut seul (refaire peut être interdit, #84) : cf. `stepAction`.
 *
 * Les libellés viennent de `home.status.*` et non des `status.*` partagés avec
 * le back-office : « Complété » / « Non commencé » sont des états de dossier,
 * écrits pour un formateur qui lit un tableau. L'apprenant lit sa propre
 * formation, on lui dit « Terminée » et « À commencer ».
 *
 * ⚠️ La couleur du badge est une couleur de STATUT, réservée : elle ne suit pas
 * la marque de l'organisation (cf. `docs/MARQUE_BLANCHE.md`). Un module
 * « terminé » affiché dans le rouge d'une marque se lirait comme un échec.
 */
export function useStatusMeta(): (status: CompletionStatus) => StatusMeta {
  const { t } = useTranslation()

  return (status) => {
    switch (status) {
      case 'COMPLETED':
        return { badgeVariant: 'successSubtle', badgeLabel: t('home.status.completed') }
      case 'INCOMPLETE':
        return { badgeVariant: 'warningSubtle', badgeLabel: t('home.inProgress') }
      default:
        return { badgeVariant: 'secondary', badgeLabel: t('home.status.notStarted') }
    }
  }
}
